import type { RowSelectionState } from "@tanstack/react-table";

/** Max rows that can be bulk-selected at once (one page, inclusive). */
export const MAX_BULK_SELECTION = 100;

export function countSelected(state: RowSelectionState | undefined): number {
  if (!state) return 0;
  return Object.values(state).filter(Boolean).length;
}

export function isBulkSelectionAtCap(
  state: RowSelectionState | undefined,
  max: number = MAX_BULK_SELECTION,
): boolean {
  return countSelected(state) >= max;
}

/**
 * Keep at most `max` selected IDs, preserving existing key order.
 */
export function clampRowSelection(
  state: RowSelectionState,
  max: number = MAX_BULK_SELECTION,
): RowSelectionState {
  const selectedIds = Object.keys(state).filter((id) => state[id]);
  if (selectedIds.length <= max) {
    return state;
  }

  const next: RowSelectionState = {};
  for (const id of selectedIds.slice(0, max)) {
    next[id] = true;
  }
  return next;
}

export function selectRowsUpToMax(
  current: RowSelectionState,
  rowIds: string[],
  max: number = MAX_BULK_SELECTION,
): RowSelectionState {
  const next: RowSelectionState = { ...current };
  let selected = countSelected(next);
  for (const id of rowIds) {
    if (next[id]) continue;
    if (selected >= max) break;
    next[id] = true;
    selected += 1;
  }
  return next;
}

export function applyPageSelectAll(
  current: RowSelectionState,
  pageRowIds: string[],
  allPageSelected: boolean,
): RowSelectionState {
  if (allPageSelected) {
    const next: RowSelectionState = { ...current };
    for (const id of pageRowIds) {
      delete next[id];
    }
    return next;
  }
  return selectRowsUpToMax(current, pageRowIds);
}
